import type { ProcessStepAssignmentStatus, ProcessStepRecording } from '@bliks-io/contracts';
import {
  LazyProcessMapDialog,
  useProcessFlowData,
  useProcessMapDialogData
} from '@/components/process-flow';

/**
 * The recording page's own view of the map — the one host whose highlighted
 * step is not in the process yet.
 *
 * `useProcessFlowData` synthesizes the in-flight recording as a placeholder
 * node instead of merging it into `processStepRecordings` (which used to draw
 * it as a finished activity and, for a first step, a duplicate START edge), so
 * the `current` highlight lands on that placeholder. That placeholder list is
 * the ONLY thing it changes about the fetched content — everything else the
 * fetcher returns reaches the dialog untouched.
 *
 * There is no host surface under this one: the page opens the map directly, so
 * both gates are the map's own `open`. The show-process query it opens is the
 * one `StepRecordingPage` already holds — same key, so the map costs a layout
 * request and nothing else.
 */
export function RecordingProcessMapDialog({
  open,
  onOpenChange,
  currentRecording,
  currentRecordingStatus
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRecording: ProcessStepRecording | null;
  /**
   * Known assignment status for `currentRecording`. When `pending-unclear`, the
   * synthesized placeholder renders as an amber "?" node.
   */
  currentRecordingStatus?: ProcessStepAssignmentStatus;
}) {
  const { content } = useProcessMapDialogData(currentRecording?.process, {
    hostOpen: open,
    mapOpen: open
  });
  const { openAssignments } = useProcessFlowData({
    process: content.process,
    openAssignments: content.openAssignments,
    currentRecording,
    currentRecordingStatus
  });

  return (
    <LazyProcessMapDialog
      {...content}
      open={open}
      onOpenChange={onOpenChange}
      openAssignments={openAssignments}
      highlightNodeId={currentRecording?._id}
    />
  );
}
